/* Apify vault — API-token + LinkedIn deep-enrichment scrape-test. */
import GenericVault from "./GenericVault";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Mail, Linkedin, Search, UserSearch, Briefcase, Bot, GraduationCap, Wrench } from "lucide-react";

/* ----------------------------------------------------------------- */
/*  ApifyVault — API-token vault + e-mail-modus toggle voor deep      */
/*  enrichment (HarvestAPI: $4/1k zonder e-mail, $10/1k mét           */
/*  e-mail-search). Geldt voor "Diep verrijken" + de scrape-test;     */
/*  wekelijkse freshness-checks blijven altijd op de goedkope modus.  */
/* ----------------------------------------------------------------- */
function ApifyVault({ provider, cfg, onSaved }) {
    const { t } = useTranslation();
    const settings = cfg.settings || {};
    const [emailMode, setEmailMode] = useState(!!settings.email_mode);
    const [saving, setSaving] = useState(false);

    useEffect(() => { setEmailMode(!!(cfg.settings || {}).email_mode); }, [cfg]);

    const toggleEmailMode = async (next) => {
        setEmailMode(next); setSaving(true);
        try {
            await api.patch("/integrations/apify", { settings: { email_mode: next } });
            toast.success(next ? t("integr.apify_email_mode_on") : t("integr.apify_email_mode_off"));
            onSaved?.();
        } catch (e) {
            setEmailMode(!next);
            toast.error(e?.response?.data?.detail || t("integr.save_failed"));
        } finally { setSaving(false); }
    };

    return (
        <div className="space-y-4">
            <GenericVault provider={provider} cfg={cfg} onSaved={onSaved} appBaseUrl="" />
            <div className="rounded-xl border border-white/10 bg-[var(--jr-surface-2)]/40 p-4"
                data-testid="apify-email-mode-panel">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-2.5 min-w-0">
                        <Mail size={15} className="text-[#97d700] mt-0.5 shrink-0" />
                        <div className="min-w-0">
                            <div className="text-xs font-mono uppercase tracking-[0.18em] text-[#97d700] inline-flex items-center gap-1.5 flex-wrap">
                                {t("integr.apify_email_mode_title")}
                                <span className={`px-1.5 py-0.5 rounded normal-case tracking-normal ${emailMode
                                    ? "bg-[#22c55e]/15 text-[#22c55e]"
                                    : "bg-white/[0.05] text-[var(--jr-text-3)]"}`}
                                    data-testid="apify-email-mode-badge">
                                    {emailMode ? "$10 / 1k" : "$4 / 1k"}
                                </span>
                            </div>
                            <p className="text-[11px] text-[var(--jr-text-3)] mt-1 leading-relaxed max-w-lg">
                                {t("integr.apify_email_mode_sub")}
                            </p>
                        </div>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer select-none shrink-0"
                        data-testid="apify-email-mode-toggle">
                        <input type="checkbox" checked={emailMode} disabled={saving || !cfg.configured}
                            onChange={(e) => toggleEmailMode(e.target.checked)}
                            className="accent-[#97d700]" />
                        <span className="text-xs text-white">{t("integr.apify_email_mode_label")}</span>
                        {saving && <Loader2 size={12} className="animate-spin text-[var(--jr-text-3)]" />}
                    </label>
                </div>
                {!cfg.configured && (
                    <p className="mt-2 text-[11px] text-[var(--jr-text-3)] italic">{t("integr.proof_not_configured")}</p>
                )}
            </div>
        </div>
    );
}
/* ----------------------------------------------------------------- */
/*  ApifyScrapeTestPanel — paste a LinkedIn profile URL, run a REAL   */
/*  deep-scrape via HarvestAPI and show the full profile returned.    */
/* ----------------------------------------------------------------- */
export function ApifyScrapeTestPanel() {
    const { t } = useTranslation();
    const [url, setUrl] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const run = async () => {
        if (!url.trim()) return;
        setBusy(true); setError(null); setResult(null);
        try {
            const r = await api.post("/integrations/apify/scrape-test", { linkedin_url: url.trim() });
            setResult(r.data);
            toast.success(t("integr.apify_test_ok", { name: r.data.profile?.name || "—" }));
        } catch (e) {
            setError(e?.response?.data?.detail || t("integr.apify_test_failed"));
        } finally { setBusy(false); }
    };

    const p = result?.profile;
    return (
        <div className="mt-3 rounded-xl border border-[#97d700]/20 bg-[#97d700]/[0.03] p-4"
            data-testid="apify-scrape-test-panel">
            <div className="flex items-start gap-2.5">
                <Bot size={16} className="text-[#97d700] mt-0.5 shrink-0" />
                <div>
                    <div className="text-xs font-mono uppercase tracking-[0.18em] text-[#97d700]">{t("integr.apify_test_title")}</div>
                    <p className="text-[11px] text-[var(--jr-text-3)] mt-0.5 max-w-md">{t("integr.apify_test_sub")}</p>
                </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
                <input value={url} onChange={(e) => setUrl(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !busy && run()}
                    placeholder={t("integr.enrich_test_placeholder")}
                    data-testid="apify-scrape-test-input"
                    className="flex-1 min-w-0 bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-xs text-white outline-none focus:border-[#97d700]"/>
                <button onClick={run} disabled={busy || !url.trim()}
                    data-testid="apify-scrape-test-run"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#97d700] text-black text-xs font-medium hover:bg-[#97d700]/85 disabled:opacity-50 shrink-0">
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
                    {busy ? t("integr.apify_test_running") : t("integr.apify_test_run")}
                </button>
            </div>

            {error && (
                <div className="mt-3 rounded-lg border border-[#ef4444]/30 bg-[#ef4444]/[0.08] p-3 text-xs inline-flex items-start gap-2"
                    data-testid="apify-scrape-test-error">
                    <AlertTriangle size={14} className="text-[#ef4444] mt-0.5 shrink-0" />
                    <span className="text-[var(--jr-text-2)]">{error}</span>
                </div>
            )}

            {p && (
                <div className="mt-3 rounded-lg border border-[#97d700]/25 bg-[var(--jr-surface-2)]/40 p-3"
                    data-testid="apify-scrape-test-result">
                    <div className="flex items-start gap-3">
                        {p.photo_url ? (
                            <img src={p.photo_url} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
                        ) : (
                            <div className="w-10 h-10 rounded-full bg-[#97d700]/15 grid place-items-center shrink-0">
                                <UserSearch size={16} className="text-[#97d700]" />
                            </div>
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium text-white" data-testid="apify-result-name">{p.name}</span>
                                {p.linkedin_url && (
                                    <a href={p.linkedin_url} target="_blank" rel="noreferrer"
                                        className="text-[#97d700] hover:text-white"><Linkedin size={12}/></a>
                                )}
                                {p.open_to_work && (
                                    <span className="text-[10px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-[#22c55e]/15 text-[#22c55e] border border-[#22c55e]/30">
                                        {t("integr.apify_open_to_work")}
                                    </span>
                                )}
                                <span className="text-[10px] font-mono text-[var(--jr-text-3)]">{result.latency_ms}ms</span>
                            </div>
                            <div className="text-[11px] text-[var(--jr-text-3)] mt-0.5">
                                {[p.title, p.company, p.location].filter(Boolean).join(" · ")}
                            </div>
                            {(p.emails || []).length > 0 && (
                                <div className="mt-1 inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded bg-[#22c55e]/12 text-[#22c55e] border border-[#22c55e]/30"
                                    data-testid="apify-result-email">
                                    <Mail size={10} /> {p.emails[0]}
                                </div>
                            )}
                            {p.headline && (
                                <div className="text-[11px] text-[var(--jr-text-2)] mt-1 italic">"{p.headline}"</div>
                            )}
                            <div className="mt-2.5 grid sm:grid-cols-2 gap-2">
                                <div className="rounded-lg border border-white/8 bg-black/20 px-3 py-2">
                                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-2)] inline-flex items-center gap-1"><Briefcase size={9}/> {t("integr.apify_experience")} ({(p.experience || []).length})</div>
                                    {(p.experience || []).slice(0, 3).map((e, i) => (
                                        <div key={i} className="text-xs text-[var(--jr-text-2)] mt-0.5 truncate" data-testid={`apify-exp-${i}`}>
                                            {e.position}{e.company ? ` · ${e.company}` : ""}{e.duration ? ` (${e.duration})` : ""}
                                        </div>
                                    ))}
                                    {(p.experience || []).length === 0 && (
                                        <div className="text-xs text-[var(--jr-text-3)] italic mt-0.5">{t("integr.apify_none")}</div>
                                    )}
                                </div>
                                <div className="rounded-lg border border-white/8 bg-black/20 px-3 py-2">
                                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-2)] inline-flex items-center gap-1"><GraduationCap size={9}/> {t("integr.apify_education")} ({(p.education || []).length})</div>
                                    {(p.education || []).slice(0, 3).map((e, i) => (
                                        <div key={i} className="text-xs text-[var(--jr-text-2)] mt-0.5 truncate">
                                            {e.school}{e.degree ? ` · ${e.degree}` : ""}
                                        </div>
                                    ))}
                                    {(p.education || []).length === 0 && (
                                        <div className="text-xs text-[var(--jr-text-3)] italic mt-0.5">{t("integr.apify_none")}</div>
                                    )}
                                </div>
                            </div>
                            {(p.skills || []).length > 0 && (
                                <div className="mt-2 rounded-lg border border-white/8 bg-black/20 px-3 py-2">
                                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-2)] inline-flex items-center gap-1"><Wrench size={9}/> {t("integr.apify_skills")} ({p.skills.length})</div>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {p.skills.slice(0, 12).map((s, i) => (
                                            <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-[#97d700]/15 text-[#97d700] border border-[#97d700]/30">{s}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ApifyVault;
